import React, { useRef, useState } from 'react';
import { omit } from 'lodash';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { useUser2 } from 'contexts/user2';
import { Avatar, Box, FeedbackTile, RichTextInput, Stack, Text } from 'shell/components';
import { TYPES } from 'utils/constants';
import { uploadFile } from 'utils/file';
import { downloadFileFromURL, getS3FileURL, getTooltipComponent, getUserPreferredFullName } from 'utils/helper';

const FeedbackList = ({
    feedback = {},
    fileList = {},
    currentAccountId,
    getUserByAccountId,
    onReply,
    onEdit,
    onDelete,
    onDeleteSubfeedback,
}) => {
    const [editingFeedback, setEditingFeedback] = useState(null);

    const feedbackData = Object.values(feedback).map((feedback) => {
        return {
            ...feedback,
            subfeedback: feedback.subfeedback
                ? Object.values(feedback.subfeedback).map((subfeedback) => {
                      return {
                          ...subfeedback,
                          isReply: true,
                          files: Object.values(fileList).filter((file) => file.parent_id === subfeedback.id),
                      };
                  })
                : [],
            files: Object.values(fileList).filter((file) => file.parent_id === feedback.id),
        };
    });

    const handleSelect = ({ key }, feedback) => {
        const { id, parent_id, isReply } = feedback;
        switch (key) {
            case 'reply': {
                onReply(feedback);
                break;
            }
            case 'edit': {
                setEditingFeedback(feedback);
                break;
            }
            case 'delete': {
                return setTimeout(() => {
                    if (isReply) {
                        onDeleteSubfeedback({ id, parentId: parent_id });
                    } else {
                        onDelete({ id });
                    }
                });
            }
            default:
                throw new Error();
        }
    };

    const renderFeedbackItem = (feedbackItem) => {
        const { active, owner_account_id } = feedbackItem;
        const author = getUserByAccountId(owner_account_id);
        const isOwner = owner_account_id === currentAccountId;

        const handleSend = (html) => {
            onEdit({ ...editingFeedback, text: html });
            setEditingFeedback(null);
        };

        const options = [
            ...(active ? [{ key: 'reply', value: 'Reply' }] : []),
            ...(isOwner
                ? [
                      { key: 'edit', value: 'Edit' },
                      { key: 'delete', value: 'Delete' },
                  ]
                : []),
        ];

        const handleSelectFile = ({ key }, file) => {
            switch (key) {
                case 'download':
                    downloadFileFromURL(getS3FileURL(file.data), file.data.name);
                    break;
                default:
                    break;
            }
        };

        return editingFeedback?.id === feedbackItem.id ? (
            <Stack direction={'row'} spacing={2}>
                <Avatar size={'small'} user={author} />
                <RichTextInput
                    autoFocus
                    defaultShowFormatting={false}
                    defaultValue={editingFeedback.text}
                    isEditing
                    onClickCancelEdit={() => setEditingFeedback(null)}
                    onSend={handleSend}
                    tabIndex={0}
                />
            </Stack>
        ) : (
            <FeedbackTile
                sx={{ minWidth: 0 }}
                author={author}
                feedback={feedbackItem}
                getFileOptions={() => [{ key: 'download', value: 'Download' }]}
                getNameById={(id) => getUserPreferredFullName(getUserByAccountId(id))}
                options={options}
                onSelect={(selected) => handleSelect(selected, feedbackItem)}
                onSelectFile={(option, file) => handleSelectFile(option, file)}
            />
        );
    };

    const renderFeedbackThread = (feedbackThreadHead) => {
        const { subfeedback } = feedbackThreadHead;

        return (
            <Stack key={feedbackThreadHead.id} spacing={2}>
                {renderFeedbackItem(feedbackThreadHead)}
                {subfeedback.map((subfeedback) => (
                    <IndentBox key={subfeedback.id}>{subfeedback.active && renderFeedbackItem(subfeedback)}</IndentBox>
                ))}
            </Stack>
        );
    };

    return (
        <Stack spacing={2}>
            {feedbackData
                .filter(
                    (feedback) =>
                        feedback.active || feedback.subfeedback.filter((subfeedback) => subfeedback.active).length
                )
                .map((feedback) => renderFeedbackThread(feedback))}
        </Stack>
    );
};

const FeedbackSection = ({
    disabled,
    taskId,
    currentAccountId,
    feedback,
    fileList,
    getUserByAccountId,
    addFeedback,
    updateFeedback,
    deleteFeedback,
    addSubfeedback,
    updateSubfeedback,
    deleteSubfeedback,
    countFeedback,
}) => {
    const { course, currentGroup } = useUser2();

    const inputRef = useRef(null);
    const [isReplyingTo, setIsReplyingTo] = useState(null);
    const [files, setFiles] = useState([]);

    const handleReply = (feedback) => {
        setIsReplyingTo(feedback);
        setTimeout(() => {
            if (inputRef.current) inputRef.current.props.editor.commands.focus();
        }, 100);
    };

    const handleEdit = (feedback) => {
        if (feedback.isReply) {
            updateSubfeedback(feedback.id, feedback.parent_id, feedback.text);
        } else {
            updateFeedback(feedback.id, feedback.text);
        }
    };

    const handleChangeFile = async (e) => {
        const files = Array.from(e.target.files);
        const filesToUpload = files.map((file) => ({
            id: uuid.v4(),
            active: true,
            owner_account_id: currentAccountId,
            course_id: course.id,
            group_id: currentGroup.id,
            created_on: new Date().getTime(),
            modified_on: new Date().getTime(),
            data: {
                name: file.name,
                size: file.size,
                type: file.type,
            },
            type: TYPES.FEEDBACK,
            public: true,
            loading: true,
        }));
        setFiles((current) => [...current, ...filesToUpload]);
        await Promise.all(
            filesToUpload.map(async (file, index) => {
                uploadFile(
                    files[index],
                    () => {},
                    (data) => {
                        setFiles((current) => {
                            return current.map((currentFile) => {
                                if (currentFile.id === file.id) {
                                    return {
                                        ...omit(currentFile, ['loading']),
                                        data: {
                                            ...currentFile.data,
                                            ...data,
                                        },
                                    };
                                } else return currentFile;
                            });
                        });
                    },
                    (e) => {
                        console.error('File upload failed.', e);
                    },
                    currentAccountId
                );
            })
        );
    };

    const handleSelect = ({ key }, file) => {
        switch (key) {
            case 'delete':
                setFiles((current) => current.filter((currentFile) => currentFile.id !== file.id));
                break;
        }
    };

    const handleSend = (html, parent) => {
        const parsedFiles = files.map((file) => ({ ...file.data }));
        if (parent) {
            if (parent.isReply) {
                addSubfeedback(taskId, parent.parent_id, html, parsedFiles);
            } else {
                addSubfeedback(taskId, parent.id, html, parsedFiles);
            }
            setIsReplyingTo(null);
        } else {
            addFeedback(taskId, html, parsedFiles);
        }
        setFiles([]);
    };

    return (
        <CustomStack disabled={disabled} spacing={2}>
            <Stack sx={{ width: 'fit-content' }}>
                <Text
                    aria-disabled={disabled}
                    data-cy={'feedback-count'}
                    tooltip={{
                        component: getTooltipComponent('Feedback', 'Leave feedback related to the task here'),
                    }}
                    variant={'p2'}
                    weight={'semibold'}
                >
                    Feedback {`(${countFeedback(taskId)})`}
                </Text>
            </Stack>
            <FeedbackList
                feedback={feedback}
                fileList={fileList}
                taskId={taskId}
                currentAccountId={currentAccountId}
                getUserByAccountId={getUserByAccountId}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={({ id }) => deleteFeedback(id)}
                onDeleteSubfeedback={({ id, parentId }) => deleteSubfeedback(id, parentId)}
            />

            <RichTextInput
                ref={inputRef}
                defaultShowFormatting={false}
                disabled={disabled}
                files={files}
                getNameById={(id) => getUserPreferredFullName(getUserByAccountId(id))}
                isReplyingTo={isReplyingTo}
                placeholder={'Add Feedback'}
                onChangeFile={handleChangeFile}
                onClickCancelReply={() => setIsReplyingTo(null)}
                onSelect={handleSelect}
                onSend={handleSend}
            />
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ disabled }) => {
    return {
        ...(disabled && {
            opacity: 0.5,
        }),
    };
});

const IndentBox = styled(Box)(({ theme }) => {
    return {
        marginLeft: theme.spacing(6),
    };
});

export default FeedbackSection;
